// Per-prefab render style for the instanced unit batches: size, hover and the
// idle / move bob. The prefab system has no animation clips (a prefab is a
// static voxel model; VoxelUnitBatch animates the whole instance), so the
// Champion Dragon's "idle" and "fly" read from a bigger, slower hover bob
// (idle) and a faster, deeper wing-beat bob (moving). Scale is a render-side
// instance scale, never a sim coordinate or HEX_SIZE (docs D12).

export interface UnitRenderStyle {
  /** Uniform instance scale. */
  readonly scale: number;
  /** Extra height above the tile surface (flyers), board units. */
  readonly lift: number;
  /** [angular frequency rad/s, amplitude] while standing still. */
  readonly idleBob: readonly [number, number];
  /** [angular frequency rad/s, amplitude] while moving. */
  readonly moveBob: readonly [number, number];
  /** Share of the standard gold selection wash (1 = full, 0 = none). A big body keeps
   * its own colour and relies on the ground ring instead. */
  readonly selectTint: number;
}

export const DEFAULT_RENDER_STYLE: UnitRenderStyle = {
  scale: 1,
  lift: 0,
  idleBob: [2, 0.02],
  moveBob: [2.7, 0.06],
  selectTint: 1,
};

/**
 * The rank-and-file army reads at x1.18 so a soldier, archer or knight stays
 * legible at play distance (a unit is ~50-70 px tall at distance 20, ~15-20 px
 * at the far zoom limit). Render-side only; never a sim coordinate. Champions
 * and flyers keep their own, larger styles below.
 */
const RANK_AND_FILE: UnitRenderStyle = { ...DEFAULT_RENDER_STYLE, scale: 1.18 };
/** Siege engines are wide already; a smaller nudge keeps them inside the tile. */
const SIEGE: UnitRenderStyle = { ...DEFAULT_RENDER_STYLE, scale: 1.1 };

const STYLES: Readonly<Record<string, UnitRenderStyle>> = {
  soldier: RANK_AND_FILE,
  bowman: RANK_AND_FILE,
  knight: RANK_AND_FILE,
  guard: RANK_AND_FILE,
  zealot: RANK_AND_FILE,
  templar: RANK_AND_FILE,
  dark_templar: RANK_AND_FILE,
  paladin: RANK_AND_FILE,
  death_knight: RANK_AND_FILE,
  catapult: SIEGE,
  // Champion Dragon: x1.6 for readability, hovering, slow glide / strong wing-beat.
  legend_dragon: {
    scale: 1.6,
    lift: 0.4,
    idleBob: [1.4, 0.06],
    moveBob: [3.4, 0.11],
    // At x1.6 even a light gold wash desaturates the wings; the ground ring and
    // outline carry the selection, so the body keeps its own colour.
    selectTint: 0,
  },
  // Champion Golem: large and heavy, no hover, a barely-there idle sway and a slow, shallow
  // stomp while moving. A big body keeps its own colour; the ground ring carries selection.
  legend_golem: {
    scale: 1.35,
    lift: 0,
    idleBob: [0.9, 0.012],
    moveBob: [1.7, 0.035],
    selectTint: 0,
  },
  // Champion Lich: a touch bigger than infantry, floating above the ground with a slow bob.
  legend_lich: {
    scale: 1.15,
    lift: 0.3,
    idleBob: [1.3, 0.05],
    moveBob: [2.0, 0.07],
    selectTint: 0.5,
  },
};

export function unitRenderStyle(prefabId: string): UnitRenderStyle {
  return STYLES[prefabId] ?? DEFAULT_RENDER_STYLE;
}

/** Per-instance `aSelected` value: 0 when unselected, else the prefab's wash share. */
export function selectionStrength(prefabId: string, selected: boolean): number {
  return selected ? unitRenderStyle(prefabId).selectTint : 0;
}
